import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BarChart3, TrendingUp, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { Label, scoreColor, EmptyState, InfoBadge } from "../../components/common";
import GoogleBenchmarks from "../../components/GoogleBenchmarks";

export default function Benchmarking() {
  const [sp] = useSearchParams();
  const wantsRefresh = sp.get("refresh") === "1";
  const [refreshing, setRefreshing] = useState(wantsRefresh);
  const [reloadKey, setReloadKey] = useState(0);

  // Force a live Google refresh when the user clicks through from a tile.
  useEffect(() => {
    if (!wantsRefresh) return;
    api.post("/admin/locations/benchmarks/refresh-all")
      .catch(() => {})
      .finally(() => {
        setRefreshing(false);
        setReloadKey((k) => k + 1);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const manualRefresh = async () => {
    setRefreshing(true);
    try { await api.post("/admin/locations/benchmarks/refresh-all"); }
    catch (e) {}
    setRefreshing(false);
    setReloadKey((k) => k + 1);
  };

  return (
    <div className="space-y-6 pb-10">
      {/* Hero — collapses tight on mobile */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <span className="flex items-center gap-1.5">
            <Label>Network Effect</Label>
            <InfoBadge title="Peer Benchmarking" testId="bench-header-info"
              text="Shows how your service compares against anonymised groups of similar businesses. You see where you rank without ever seeing who the competitors are, and small groups are hidden to protect everyone's privacy." />
          </span>
          <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Peer Benchmarking</h1>
          <p className="mt-1 text-sm text-slate-500">
            Compared against anonymised peer cohorts. Competitor identity is never exposed and cohorts below the minimum size are suppressed.
          </p>
        </div>
        <button
          type="button"
          onClick={manualRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-60"
          data-testid="bench-refresh"
        >
          <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Fetching latest from Google…" : "Refresh from Google"}
        </button>
      </div>

      <PeerCohorts />

      <div className="h-px bg-slate-100" />

      <GoogleBenchmarks key={reloadKey} />
    </div>
  );
}

/** Anonymised cohort section — self-contained; failures/slowness don't
 *  block the rest of the page.
 */
function PeerCohorts() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/benchmarks").then((r) => setD(r.data)).catch(() => setErr(true));
  }, []);

  if (err) return null;
  if (!d) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-400" data-testid="peer-cohorts-loading">
        Loading peer comparison…
      </div>
    );
  }

  return (
    <>
      <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6">
        <span className="flex items-center gap-1.5">
          <Label>Your Organisation Service Index</Label>
          <InfoBadge title="Your Organisation Service Index" testId="bench-index-info"
            text="Your group's overall service score, used as the basis for every peer comparison below. It's the same headline number shown on your dashboard." />
        </span>
        <div className="mt-2 flex flex-wrap items-end gap-x-3 gap-y-1">
          <span className="sv-metric text-4xl font-bold sm:text-5xl" style={{ color: scoreColor(d.your_index) }}>
            {d.your_index.toFixed(2)}
          </span>
          <span className="pb-2 text-xs text-slate-400 sm:text-sm">
            from {d.verified_interactions} verified interactions
          </span>
        </div>
      </div>

      {d.insufficient ? (
        <EmptyState icon={BarChart3} title="Not enough peer data yet" hint="Benchmarks appear once a cohort reaches the minimum size to protect anonymity." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {d.cohorts.map((c, i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`cohort-${i}`}>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <Label>{c.label}</Label>
                  <InfoBadge title="Percentile" testId={`cohort-${i}-info`}
                    text="Your percentile shows the share of peers in this group you're ahead of — e.g. 80th means you're doing better than 80% of similar businesses. 'Top 25%/10%' are the scores you'd need to reach those tiers." />
                </span>
                <span className="text-xs text-slate-400">n={c.cohort_size}</span>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400">Your percentile</p>
                  <p className="sv-metric text-3xl font-bold text-[#4338CA]">
                    {c.your_percentile}<span className="text-lg">th</span>
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-indigo-200" />
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <Row label="Your index" value={c.your_index.toFixed(2)} bold />
                <Row label="Peer average" value={c.peer_average.toFixed(2)} />
                <Row label="Top 25%" value={c.top_25.toFixed(2)} />
                <Row label="Top 10%" value={c.top_10.toFixed(2)} />
              </div>
              <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-[#4338CA]" style={{ width: `${c.your_percentile}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-500">{label}</span>
      <span className={`sv-metric ${bold ? "font-bold text-slate-900" : "text-slate-600"}`}>{value}</span>
    </div>
  );
}
